// Le message à montrer quand un appel à l'API échoue.
//
// Trois formes de corps d'erreur cohabitent côté serveur, et rien ne les unifiera de
// sitôt — la première appartient à une gem, la troisième à Rails lui-même :
//
//   devise_token_auth   { success: false, errors: ['Adresse courriel inconnue.'] }
//                       { success: false, errors: { password: [...], full_messages: [...] } }
//   nos contrôleurs     { error: "L'abonnement de cette entreprise n'est plus actif." }
//   l'infrastructure    { error: 'Internal Server Error', status: 500 }   ← Rails
//                       { error: 'Trop de tentatives…', status: 429 }     ← Rack::Attack
//
// L'écran « mot de passe oublié » ne lisait que `errors`. Quand les routes
// d'authentification de staging se sont mises à répondre 500 (2026-08-02, migrations
// non jouées), il affichait donc `undefined` : une pastille rouge avec un point
// d'exclamation et pas un mot. Le vide est le pire des messages — il se lit comme
// « c'est vous qui avez mal fait quelque chose », et il a fait chercher le bogue du
// côté du mot de passe alors que toute l'authentification était par terre.

import { contactEmail } from './configuration';

export const DEFAULT_ERROR_MESSAGE = 'Une erreur est survenue. Veuillez réessayer.';

// Une panne ne se raconte pas avec les mots du serveur : « Internal Server Error »
// n'est ni en français, ni quelque chose que la personne devant l'écran peut corriger.
// On dit ce qui est vrai — ça ne vient pas d'elle — et où aller si ça dure.
const SERVER_ERROR_MESSAGE = `Le service est momentanément indisponible. Réessayez dans quelques minutes; si le problème persiste, écrivez-nous à ${contactEmail}.`;

// Le serveur n'a jamais répondu : coupure, DNS, CORS. `request.js` a déjà journalisé
// l'erreur technique dans la console.
const NETWORK_ERROR_MESSAGE = 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.';

// Une phrase, quelle que soit la forme reçue. Les listes sont recollées avec une espace :
// rendues telles quelles par React, elles s'affichaient soudées les unes aux autres.
const phrase = (valeur) => {
  if (typeof valeur === 'string') return valeur.trim();
  if (Array.isArray(valeur)) {
    return valeur.filter((part) => typeof part === 'string' && part.trim()).join(' ').trim();
  }
  return '';
};

// `body` est le corps JSON déjà lu, `status` le code HTTP. Passer `false`/`null` en
// corps — ce que renvoient nos actions quand `fetch` a échoué — donne le message réseau.
export const apiErrorMessage = (body, status) => {
  if (body === false || body === null || body === undefined) return NETWORK_ERROR_MESSAGE;
  if (status >= 500) return SERVER_ERROR_MESSAGE;
  if (typeof body !== 'object') return DEFAULT_ERROR_MESSAGE;

  const { errors, error, message } = body;

  return phrase(errors)
    || phrase(errors && errors.full_messages)
    || phrase(error)
    || phrase(message)
    || DEFAULT_ERROR_MESSAGE;
};

// Exporté pour le garde-fou de SnackBarAlert, qui reçoit un message déjà composé et doit
// seulement savoir s'il ne s'affichera pas.
//
// Volontairement PRUDENT sur ce qui n'est ni chaîne ni liste : un nombre ou un fragment
// JSX s'affiche très bien, et la barrière ne doit pas avaler un message valide sous
// prétexte qu'elle ne sait pas le lire.
const displayable = (part) => {
  if (typeof part === 'string') return part.trim() !== '';
  if (typeof part === 'boolean') return false;
  return part !== null && part !== undefined && part !== '';
};

export const messageIsEmpty = (message) => {
  if (Array.isArray(message)) return !message.some(displayable);
  return !displayable(message);
};
